'use client'

import {useCallback, useEffect, useRef} from 'react'

const DIRECT_POINTER_MOVE_TOLERANCE_PX = 10

type DirectPointerGesture = {
    pointerId: number
    pointerType: 'touch' | 'pen'
    clientX: number
    clientY: number
    cancelled: boolean
}

const isDirectPointer = (pointerType: string): pointerType is DirectPointerGesture['pointerType'] =>
    pointerType === 'touch' || pointerType === 'pen'

export function useSelectionPointerCommit(
    onCommit: () => void,
    disabled: boolean,
    suppressNextCompatibleClick: (event: {clientX: number; clientY: number}) => void,
) {
    const gestureRef = useRef<DirectPointerGesture | null>(null)
    const stopScrollListenerRef = useRef<(() => void) | null>(null)
    const stopScrollListener = useCallback(() => {
        stopScrollListenerRef.current?.()
        stopScrollListenerRef.current = null
    }, [])
    useEffect(() => stopScrollListener, [stopScrollListener])

    const onPointerDown = useCallback((event: React.PointerEvent<HTMLElement>) => {
        if (!event.isPrimary || event.button !== 0) return
        stopScrollListener()
        gestureRef.current = null
        if (disabled || !isDirectPointer(event.pointerType)) return

        gestureRef.current = {
            pointerId: event.pointerId,
            pointerType: event.pointerType,
            clientX: event.clientX,
            clientY: event.clientY,
            cancelled: false,
        }
        const cancelAfterScroll = () => {
            if (gestureRef.current) gestureRef.current.cancelled = true
        }
        document.addEventListener('scroll', cancelAfterScroll, true)
        stopScrollListenerRef.current = () => document.removeEventListener('scroll', cancelAfterScroll, true)
    }, [disabled, stopScrollListener])

    const onPointerMove = useCallback((event: React.PointerEvent<HTMLElement>) => {
        const gesture = gestureRef.current
        if (!gesture || gesture.pointerId !== event.pointerId) return

        if (Math.hypot(event.clientX - gesture.clientX, event.clientY - gesture.clientY) > DIRECT_POINTER_MOVE_TOLERANCE_PX) {
            gesture.cancelled = true
        }
    }, [])

    const onPointerUp = useCallback((event: React.PointerEvent<HTMLElement>) => {
        const gesture = gestureRef.current
        if (!gesture || gesture.pointerId !== event.pointerId) return
        gestureRef.current = null
        stopScrollListener()

        // A dragged pen may still produce click; suppress it even when selection
        // is cancelled. pointerup.preventDefault() alone does not cancel click.
        event.preventDefault()
        event.stopPropagation()
        suppressNextCompatibleClick(event)
        if (
            disabled || gesture.cancelled || !event.isPrimary || event.button !== 0
            || Math.hypot(event.clientX - gesture.clientX, event.clientY - gesture.clientY) > DIRECT_POINTER_MOVE_TOLERANCE_PX
        ) return
        onCommit()
    }, [disabled, onCommit, stopScrollListener, suppressNextCompatibleClick])

    const onPointerCancel = useCallback((event: React.PointerEvent<HTMLElement>) => {
        if (gestureRef.current?.pointerId === event.pointerId) {
            gestureRef.current = null
            stopScrollListener()
        }
    }, [stopScrollListener])

    const onLostPointerCapture = useCallback((event: React.PointerEvent<HTMLElement>) => {
        if (gestureRef.current?.pointerId === event.pointerId) {
            gestureRef.current.cancelled = true
            stopScrollListener()
        }
    }, [stopScrollListener])

    const onClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        if (disabled) return

        event.stopPropagation()
        onCommit()
    }, [disabled, onCommit])

    return {
        'data-fitro-pointer-commit': 'true' as const,
        onClick,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel,
        onLostPointerCapture,
    }
}
